import React, { useState, useRef, useEffect } from "react"
import {
  Col, Form, Card, Radio, Row,
} from "antd"
import _ from "underscore"
import TextareaAutosize from "react-textarea-autosize"
import VoiceInput from "../shared/VoiceInput"
import AIEnhanceInput from "../shared/AIEnhanceInput"
import AIEnhancedTextDisplay from "../shared/AIEnhancedTextDisplay"
import componentStyles from "../../styles"
import rules from "../../validations"
import commonStrings from "../../commonStrings"
import recording from "../../../assets/images/recording-dot.png"

function SELIndicator({
  form, item, handleOnChange, value, removeStandard,
}) {
  const standards = (key) => {
    const hash = {}
    _.pluck(window.gon.SEL_STANDARDS, "standards").forEach((array) => {
      _.extend(hash, array)
    })
    if (!key) return hash
    const standard = hash[key]
    if (!standard) return ""
    return (
      standard[
        item.adult ? "adult_indicator_language" : "student_indicator_language"
      ] || ""
    )
  }
  const [explanation, setExplanation] = useState(
    item.sel_standards[value].connection_explanation || "",
  )
  const [enhancedText, setEnhancedText] = useState("")
  const [isRecording, setIsRecording] = useState(false)
  const [isVoiceEnhancing, setIsVoiceEnhancing] = useState(false)
  const [isAIEnhancing, setIsAIEnhancing] = useState(false)
  const controlRef = useRef()
  useEffect(() => {
    setExplanation(item.sel_standards[value].connection_explanation || "")
  }, [item.sel_standards, value])

  // Close AI enhanced text display when user starts recording
  useEffect(() => {
    if (isRecording && enhancedText) {
      setEnhancedText("")
    }
  }, [isRecording, enhancedText])
  const handleValueChange = (e) => {
    setExplanation(e.target.value)
    // Clear enhanced text display when user types in input field
    if (enhancedText) {
      setEnhancedText("")
    }
    handleOnChange(e)
  }

  const applyEnhancement = () => {
    if (enhancedText) {
      setExplanation(enhancedText)
      setEnhancedText("")
    }
  }

  const handleVoiceTranscription = (transcription) => {
    // Get the base predefined text (without any existing voice input)
    const baseText = `${item.activity} ${
      item.sel_standards[value].connection_type
    } ${standards(value)
      .replace(/[^\w\s]+$/, "")
      .toLowerCase()} by`

    // Always append the transcription after the base text
    const updatedExplanation = `${baseText} ${transcription}`

    setExplanation(updatedExplanation)

    // Create synthetic event that mimics a real DOM event
    const syntheticEvent = {
      target: {
        name: "connection_explanation",
        value: updatedExplanation,
        getAttribute: (attr) => {
          if (attr === "data-sel-standard-key") return value
          return null
        },
        dataset: { selStandardKey: value },
      },
    }
    handleOnChange(syntheticEvent)
  }

  const handleAIEnhancement = (enhancedText) => {
    console.log("🔍 SELIndicator handleAIEnhancement Debug:", {
      enhancedText,
      type: typeof enhancedText,
    })
    setEnhancedText(enhancedText)

    // Create synthetic event that mimics a real DOM event
    const syntheticEvent = {
      target: {
        name: "connection_explanation",
        value: enhancedText,
        getAttribute: (attr) => {
          if (attr === "data-sel-standard-key") return value
          return null
        },
        dataset: { selStandardKey: value },
      },
    }
    handleOnChange(syntheticEvent)
  }
  const handleOnChangeRadio = (e) => {
    // Close AI Enhanced Text section when switching between Promotes and Reinforces
    if (enhancedText) {
      setEnhancedText("")
    }

    let temp = ""
    if (item.sel_standards[value].connection_explanation) {
      if (
        item.sel_standards[value].connection_explanation.includes("promotes")
      ) {
        temp = item.sel_standards[value].connection_explanation.replace(
          "promotes",
          e.target.value,
        )
      } else if (
        item.sel_standards[value].connection_explanation.includes("reinforces")
      ) {
        temp = item.sel_standards[value].connection_explanation.replace(
          "reinforces",
          e.target.value,
        )
      }
    }
    setExplanation(temp)
    const newItem = { ...item }
    newItem.sel_standards[value][controlRef.current.name] = temp
    APIClient.put(`/program_matrix/program_matrix_items/${newItem.id}`, {
      sel_standards: newItem.sel_standards,
    })
    handleOnChange(e)
  }
  const connectionsInputs = () => (
    <div key={value} className="sel-standard">
      <div className="form-group">
        <Form.Item
          name={`${value}}`}
          rules={rules.selectRequired}
          initialValue={item.sel_standards[value].connection_type || "promotes"}
        >
          <Radio.Group
            onChange={handleOnChangeRadio}
            name="connection_type"
            value={item.sel_standards[value].connection_type || ""}
          >
            <Radio name="radio1" value="promotes" ast={value}>
              {commonStrings.promotesCap}
            </Radio>
            <Radio name="radio2" value="reinforces" ast={value}>
              {commonStrings.reinforcesCap}
            </Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      <div className="hint">
        <ul>
          <li>
            <span className="font-weight-bold">
              {commonStrings.promotesCap}
            </span>
            : {commonStrings.socialEmotional}
          </li>
          <li>
            <span className="font-weight-bold">
              {commonStrings.reinforcesCap}
            </span>
            : {commonStrings.socialEmotionalExperience}
          </li>
        </ul>
      </div>
      <div className="form-group-sel">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "white",
            padding: "8px",
            borderTop: "1px solid black",
            borderLeft: "1px solid black",
            borderRight: "1px solid black",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <Col xs={20} style={{ ...componentStyles.mb5, margin: 0 }}>
            <span style={componentStyles.titleStyleSEL}>
              {`Describe how this activity ${item.sel_standards[value].connection_type} this`}
            </span>
          </Col>
          <Col
            xs={4}
            style={{
              margin: 0,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <VoiceInput
              onTranscriptionComplete={handleVoiceTranscription}
              onRecordingStateChange={(recording, status) => {
                setIsRecording(recording)
                setIsVoiceEnhancing(status === "transcribing")
              }}
              disabled={isVoiceEnhancing || isAIEnhancing}
            />
            <AIEnhanceInput
              onEnhancementComplete={handleAIEnhancement}
              currentValue={explanation}
              onEnhancementStateChange={(isEnhancing) => {
                setIsAIEnhancing(isEnhancing)
              }}
              disabled={isRecording || isVoiceEnhancing || isAIEnhancing}
            />
          </Col>
        </div>
      </div>
      <div style={{ position: "relative" }}>
        <TextareaAutosize
          ref={controlRef}
          style={componentStyles.inputStylePromoteReinforce}
          className="form-control"
          id="connection_explanation"
          name="connection_explanation"
          value={
            explanation
            || `${item.activity} ${
              item.sel_standards[value].connection_type
            } ${standards(value)
              .replace(/[^\w\s]+$/, "")
              .toLowerCase()} by`
          }
          data-sel-standard-key={value}
          onChange={handleValueChange}
        />
      </div>
      {isRecording && (
        <div className="indicator-recording-status">
          <img
            src={recording}
            alt="Recording"
            className="indicator-recording-dot"
          />
          <span>Recording...</span>
        </div>
      )}

      {/* AI Enhanced Text Display */}
      <AIEnhancedTextDisplay
        enhancedText={enhancedText}
        onApplyEnhancement={applyEnhancement}
        onCancel={() => setEnhancedText("")}
        isRecording={isRecording}
      />

      {!item.sel_standards[value].connection_explanation && (
        <p className="text-danger p-2">{commonStrings.completePrompt}</p>
      )}
    </div>
  )

  return (
    <>
      <Col className="container sel_Indicator" xs={24} sm={24} md={24} lg={24}>
        <Card
          style={componentStyles.cardStyle}
          title={(
            <Row justify="space-between">
              <Col sm={23}>
                <h5>
                  {value}. {standards(value)}
                </h5>
              </Col>
              <Col xs={1}>
                <i
                  style={componentStyles.changeCursor}
                  className="fa-solid fa-trash-can ml-2"
                  onClick={() => removeStandard(value)}
                />
              </Col>
            </Row>
          )}
        >
          <Form layout="vertical" form={form}>
            {connectionsInputs()}
          </Form>
        </Card>
      </Col>
    </>
  )
}

export default SELIndicator
